/**
 * Options Pricer — Floating Widget
 * Black-Scholes fiyat / IV / Greeks hesaplayıcı.
 * Spot fiyatı backend üzerinden TradingView / Binance'tan çeker.
 * Matematik tamamen frontend'de JS ile çalışır.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Calculator, X, Minus, RefreshCw, ChevronDown } from 'lucide-react';
import { useWS } from '../WSContext';

// ─── Black-Scholes — saf TypeScript ──────────────────────────────────────────

function erf(x: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t
    - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
}
function normCDF(x: number): number { return (1 + erf(x / Math.sqrt(2))) / 2; }
function normPDF(x: number): number { return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI); }

function bsPrice(S: number, K: number, T: number, r: number, sigma: number, type: 'call' | 'put'): number {
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const disc = Math.exp(-r * T);
  return type === 'call'
    ? S * normCDF(d1) - K * disc * normCDF(d2)
    : K * disc * normCDF(-d2) - S * normCDF(-d1);
}

function impliedVol(mkt: number, S: number, K: number, T: number, r: number, type: 'call' | 'put'): number | null {
  let sigma = Math.max(0.001, Math.min(Math.sqrt(2 * Math.PI / T) * mkt / S, 20));
  for (let i = 0; i < 200; i++) {
    const price = bsPrice(S, K, T, r, sigma, type);
    const diff = price - mkt;
    if (Math.abs(diff) < 1e-7) return sigma;
    const sqrtT = Math.sqrt(T);
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
    const vega = S * normPDF(d1) * sqrtT;
    if (vega < 1e-10) { sigma = Math.max(0.001, Math.min(sigma * (diff > 0 ? 0.5 : 1.5), 20)); continue; }
    sigma = Math.max(0.001, Math.min(sigma - diff / vega, 20));
  }
  return null;
}

interface Greeks { delta: number; gamma: number; theta: number; vega: number; rho: number; }

function bsGreeks(S: number, K: number, T: number, r: number, sigma: number, type: 'call' | 'put'): Greeks {
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const disc = Math.exp(-r * T);
  const pdf1 = normPDF(d1);
  const delta = type === 'call' ? normCDF(d1) : normCDF(d1) - 1;
  const gamma = pdf1 / (S * sigma * sqrtT);
  const tCommon = -(S * pdf1 * sigma) / (2 * sqrtT);
  const theta = type === 'call'
    ? (tCommon - r * K * disc * normCDF(d2)) / 365
    : (tCommon + r * K * disc * normCDF(-d2)) / 365;
  const vega = S * pdf1 * sqrtT / 100;
  const rho  = type === 'call'
    ? K * T * disc * normCDF(d2) / 100
    : -K * T * disc * normCDF(-d2) / 100;
  return { delta, gamma, theta, vega, rho };
}

// ─── Sayı formatlayıcılar ────────────────────────────────────────────────────

function fmt(n: number, dec = 4): string {
  if (!isFinite(n)) return '—';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtPrice(n: number): string {
  if (!isFinite(n)) return '—';
  const dec = n >= 1000 ? 2 : n >= 1 ? 4 : 6;
  return n.toLocaleString('tr-TR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// ─── Tipler ───────────────────────────────────────────────────────────────────

interface Result {
  price: number;
  iv: number | null;
  sigmaUsed: number;
  intrinsic: number;
  timeValue: number;
  greeks: Greeks;
}

const EXCHANGES = [
  { value: 'BINANCE',  label: 'Binance'  },
  { value: 'BYBIT',    label: 'Bybit'    },
  { value: 'BIST',     label: 'BIST'     },
  { value: 'NASDAQ',   label: 'NASDAQ'   },
  { value: 'NYSE',     label: 'NYSE'     },
  { value: 'CRYPTO',   label: 'Crypto'   },
];

const CALENDARS: Record<string, number> = { actual: 365, bist: 252, crypto: 365 };

// ─── localStorage kalıcılığı ──────────────────────────────────────────────────

const LS_KEY = 'options_widget_v1';
function loadLS(): Record<string, any> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}

// ─── Moneyness ───────────────────────────────────────────────────────────────

function moneyness(S: number, K: number, type: 'call' | 'put'): { label: string; color: string } {
  const ratio = S / K;
  const atmBand = 0.005; // ±%0.5 ATM kabul
  if (Math.abs(ratio - 1) <= atmBand) return { label: 'ATM', color: 'var(--t-amber, #f0c040)' };
  const itm = type === 'call' ? S > K : S < K;
  return itm
    ? { label: 'ITM', color: 'var(--t-up, #40d080)' }
    : { label: 'OTM', color: 'var(--t-down, #e07050)' };
}

// ─── Widget bileşeni ─────────────────────────────────────────────────────────

export default function OptionsWidget({ dark }: { dark: boolean }) {
  const { sendWs, subscribe } = useWS();
  const ls = useRef(loadLS()).current;

  // Pencere durumu
  const [open,      setOpen]      = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Pozisyon (drag)
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const posInit = useRef(false);
  const dragging   = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Başlangıç pozisyonu — sağ alt köşe
  useEffect(() => {
    if (!posInit.current) {
      setPos({ x: window.innerWidth - 380 - 16, y: window.innerHeight - 540 - 16 });
      posInit.current = true;
    }
  }, []);

  // Drag handlers
  const onHeaderMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    dragging.current   = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: Math.max(0, Math.min(e.clientX - dragOffset.current.x, window.innerWidth  - 380)),
        y: Math.max(0, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - 40)),
      });
    };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, []);

  // Form state (son kullanılan değerler localStorage'dan)
  const [symbol,    setSymbol]    = useState<string>(ls.symbol   ?? 'BTC');
  const [exchange,  setExchange]  = useState<string>(ls.exchange ?? 'BINANCE');
  const [spot,      setSpot]      = useState('');
  const [strike,    setStrike]    = useState('');
  const [days,      setDays]      = useState<string>(ls.days ?? '30');
  const [rate,      setRate]      = useState<string>(ls.rate ?? '5');
  const [optType,   setOptType]   = useState<'call' | 'put'>(ls.optType ?? 'call');
  const [mode,      setMode]      = useState<'iv2price' | 'price2iv'>('iv2price');
  const [iv,        setIv]        = useState<string>(ls.iv ?? '');
  const [mktPrice,  setMktPrice]  = useState('');
  const [calendar,  setCalendar]  = useState<'actual' | 'bist' | 'crypto'>(ls.calendar ?? 'actual');

  // Sonuç & yükleme durumu
  const [result,      setResult]      = useState<Result | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [fetchingSpot, setFetchingSpot] = useState(false);

  // Beklenen sembol (yarış koşulu önleme) + fetch timeout
  const pendingSymbol = useRef<string | null>(null);
  const fetchTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Form değişimlerini localStorage'a kaydet (spot ve mktPrice hariç — bunlar geçici)
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({
      symbol, exchange, days, rate, optType, iv, calendar,
    }));
  }, [symbol, exchange, days, rate, optType, iv, calendar]);

  // Binance/Bybit için eksik USDT suffix'ini otomatik ekle
  const resolveSymbol = (sym: string, exc: string): string => {
    const s = sym.trim().toUpperCase();
    const USDT_EXCHANGES = new Set(['BINANCE', 'BYBIT', 'GATE', 'OKX']);
    const QUOTE_CURRENCIES = ['USDT', 'USDC', 'BTC', 'ETH', 'BNB', 'BUSD', 'PERP'];
    if (USDT_EXCHANGES.has(exc.toUpperCase()) && !QUOTE_CURRENCIES.some(q => s.endsWith(q))) {
      return s + 'USDT';
    }
    return s;
  };

  // Spot fiyat WS aboneliği — yalnızca beklenen sembolün cevabını kabul et
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type !== 'spot_price') return;
      const respSym = (data.payload?.symbol || '').toUpperCase();
      // Beklenen sembolle eşleşmiyorsa (geç gelen eski cevap) yoksay
      if (pendingSymbol.current && respSym && respSym !== pendingSymbol.current) return;

      if (fetchTimer.current) { clearTimeout(fetchTimer.current); fetchTimer.current = null; }
      pendingSymbol.current = null;
      setFetchingSpot(false);

      if (data.payload?.price != null) {
        const px = Number(data.payload.price);
        setSpot(String(px));
        // Strike boşsa ATM strike öner
        setStrike(prev => (prev.trim() === '' ? String(px) : prev));
      } else {
        setError('Fiyat bulunamadı — sembol/borsa kontrol edin');
      }
    });
  }, [subscribe]);

  // Component unmount — bekleyen timer'ı temizle
  useEffect(() => () => { if (fetchTimer.current) clearTimeout(fetchTimer.current); }, []);

  // Tarayıcıdan direkt fiyat çek — CORS destekleyen borsalar (hangi WS endpoint'e bağlı olursa olsun çalışır)
  const fetchSpotDirect = async (sym: string, exc: string): Promise<number | null> => {
    try {
      if (['BINANCE','CRYPTO','GATE','OKX'].includes(exc)) {
        const r = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${sym}`, { signal: AbortSignal.timeout(6000) });
        const d = await r.json();
        return d.price ? parseFloat(d.price) : null;
      }
      if (exc === 'BYBIT') {
        const r = await fetch(`https://api.bybit.com/v5/market/tickers?category=spot&symbol=${sym}`, { signal: AbortSignal.timeout(6000) });
        const d = await r.json();
        return d.result?.list?.[0]?.lastPrice ? parseFloat(d.result.list[0].lastPrice) : null;
      }
    } catch { /* fallback to backend */ }
    return null;
  };

  const fetchSpot = async () => {
    if (!symbol.trim()) { setError('Sembol gerekli'); return; }
    const exc      = exchange.toUpperCase();
    const resolved = resolveSymbol(symbol, exc);
    setFetchingSpot(true);
    setError(null);

    // 1. Tarayıcıdan direkt dene (Binance/Bybit — CORS OK)
    const direct = await fetchSpotDirect(resolved, exc);
    if (direct !== null) {
      setSpot(String(direct));
      setStrike(prev => prev.trim() === '' ? String(direct) : prev);
      setFetchingSpot(false);
      return;
    }

    // 2. Backend WS üzerinden dene (BIST/NASDAQ vb.)
    pendingSymbol.current = resolved;
    sendWs({ type: 'get_spot_price', payload: { symbol: resolved, exchange: exc } });
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(() => {
      if (pendingSymbol.current === resolved) {
        pendingSymbol.current = null;
        setFetchingSpot(false);
        setError('Fiyat alınamadı — sembol/borsa kontrol edin');
      }
    }, 8000);
  };

  const calculate = useCallback(() => {
    setError(null);
    setResult(null);
    const S = parseFloat(spot);
    const K = parseFloat(strike);
    const d = parseFloat(days);
    const r = parseFloat(rate) / 100;
    if (!isFinite(S) || S <= 0)          { setError('Geçerli spot fiyat girin'); return; }
    if (!isFinite(K) || K <= 0)          { setError('Geçerli strike girin'); return; }
    if (!isFinite(d) || d <= 0)          { setError('Geçerli vade (gün) girin'); return; }
    if (!isFinite(r) || r < 0)           { setError('Geçerli faiz oranı girin'); return; }

    const T = d / CALENDARS[calendar];
    let sigmaUsed: number;
    let ivCalc: number | null = null;

    try {
      if (mode === 'iv2price') {
        const sigmaIn = parseFloat(iv) / 100;
        if (!isFinite(sigmaIn) || sigmaIn <= 0) { setError('Geçerli IV girin'); return; }
        sigmaUsed = sigmaIn;
      } else {
        const mkt = parseFloat(mktPrice);
        if (!isFinite(mkt) || mkt <= 0) { setError('Geçerli piyasa fiyatı girin'); return; }
        const computed = impliedVol(mkt, S, K, T, r, optType);
        if (computed === null) { setError('IV yakınsamadı — parametreleri kontrol edin'); return; }
        ivCalc    = computed;
        sigmaUsed = computed;
      }

      const price    = bsPrice(S, K, T, r, sigmaUsed, optType);
      const greeks   = bsGreeks(S, K, T, r, sigmaUsed, optType);
      const disc     = Math.exp(-r * T);
      const intrinsic = optType === 'call' ? Math.max(S - K * disc, 0) : Math.max(K * disc - S, 0);
      setResult({
        price, iv: ivCalc, sigmaUsed,
        intrinsic, timeValue: Math.max(price - intrinsic, 0),
        greeks,
      });
    } catch (e: any) {
      setError(e.message || 'Hesaplama hatası');
    }
  }, [spot, strike, days, rate, iv, mktPrice, optType, mode, calendar]);

  // Canlı hesaplama — girdiler değişince 300ms debounce ile otomatik recalc.
  // Yalnızca gerekli alanlar doluysa çalışır (boş formda hata göstermez).
  useEffect(() => {
    const ready = spot.trim() && strike.trim() && days.trim() && rate.trim() &&
      (mode === 'iv2price' ? iv.trim() : mktPrice.trim());
    if (!ready) return;
    const tid = setTimeout(() => calculate(), 300);
    return () => clearTimeout(tid);
  }, [spot, strike, days, rate, iv, mktPrice, optType, mode, calendar, calculate]);

  // ─── Renk paleti ──────────────────────────────────────────────────────────
  // Bu bileşen renklerini SINIF ADIYLA değil inline `style` ile veriyor, bu yüzden
  // `terminal.css`'teki sınıf eşlemeleri ona hiç ulaşmıyordu: terminal teması
  // açıkken ekran eski mavi temada kalıyordu (06.10.2026'da bildirildi).
  // Çözüm: her renk `var(--t-…, eski-hex)`. Terminal teması değişkeni tanımlıyorsa
  // oradan gelir, tanımlamıyorsa (koyu/açık tema) eski hex'e düşer — yani diğer iki
  // temanın görünümü BİREBİR aynı kalıyor ve terminal paleti tek yerde (terminal.css)
  // tanımlı kalıyor. Hex'leri burada ikinci kez yazmak, paletin iki kopyası demekti.
  const v = (token: string, fallback: string) => `var(${token}, ${fallback})`;
  const bg        = v('--t-bg',       dark ? '#070e24' : '#f0f4ff');
  const bgCard    = v('--t-panel',    dark ? '#0d1b3e' : '#ffffff');
  const border    = v('--t-line',     dark ? '#1a3060' : '#c5d3e8');
  const text      = v('--t-white',    dark ? '#e2eaf8' : '#1e2a4a');
  const muted     = v('--t-amber-dd', dark ? '#4a6fa5' : '#6b82a8');
  const accent    = v('--t-amber',    dark ? '#4a9fe8' : '#003087');
  const gold      = v('--t-amber',    dark ? '#f0c040' : '#2980b9');
  const btnBg     = v('--t-panel2',   dark ? '#1a3a70' : '#003087');
  const btnHover  = v('--t-line',     dark ? '#2050a0' : '#0050c8');
  const inputBg   = v('--t-input',    dark ? '#0a1830' : '#f8faff');
  const inputBdr  = v('--t-line2',    dark ? '#1a3060' : '#aabbd8');
  // Anlamlı renkler: call/ITM = pozitif, put/OTM/hata = negatif. Terminal temasında
  // çam yeşili ve kırmızıya, zeminleri de aynı ailenin ince tint'ine iniyor.
  const posFg     = v('--t-up',        '#40d080');
  const negFg     = v('--t-down',      '#e05060');
  const otmFg     = v('--t-down',      '#e07050');
  const posBg     = v('--t-up-tint',   '#0d4020');
  const negBg     = v('--t-down-tint', '#3a0a10');
  const errBg     = v('--t-down-tint', '#2a0a12');

  const inp = {
    background: inputBg, border: `1px solid ${inputBdr}`, borderRadius: 6,
    color: text, padding: '4px 8px', fontSize: 12, outline: 'none', width: '100%',
  } as React.CSSProperties;

  const label = { fontSize: 10, color: muted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 2 };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
          width: 44, height: 44, borderRadius: '50%',
          background: btnBg, border: `1px solid ${accent}40`,
          color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          transition: 'all 0.15s',
        }}
        title="Options Pricer"
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = btnHover; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = btnBg; }}
      >
        <Calculator size={18} />
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999,
        width: 368, background: bg, border: `1px solid ${border}`,
        borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        // Inline fontFamily, stylesheet'teki monospace kuralını YENER (inline > kural).
        // Bu yüzden yazı tipi de değişkenden geliyor; terminal temasında monospace.
        fontFamily: 'var(--t-font, system-ui, sans-serif)', userSelect: 'none',
      }}
    >
      {/* Header (draggable) */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: `1px solid ${border}`,
          cursor: 'grab', background: bgCard, borderRadius: '10px 10px 0 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calculator size={14} color={accent} />
          <span style={{ fontSize: 12, fontWeight: 700, color: accent, letterSpacing: '0.06em' }}>
            OPTIONS PRICER
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setMinimized(m => !m)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: '2px 4px', borderRadius: 4 }}
            title={minimized ? 'Genişlet' : 'Küçült'}
          >
            <Minus size={12} />
          </button>
          <button onClick={() => { setOpen(false); setResult(null); setError(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: '2px 4px', borderRadius: 4 }}
            title="Kapat"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {!minimized && (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Sembol + Borsa */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
            <div>
              <div style={label}>Sembol</div>
              <input style={inp} value={symbol}
                onChange={e => setSymbol(e.target.value.toUpperCase())}
                placeholder="SOL, BTC, THYAO…"
              />
            </div>
            <div>
              <div style={label}>Borsa</div>
              <div style={{ position: 'relative' }}>
                <select
                  value={exchange}
                  onChange={e => setExchange(e.target.value)}
                  style={{ ...inp, paddingRight: 20, appearance: 'none', cursor: 'pointer' }}
                >
                  {EXCHANGES.map(ex => (
                    <option key={ex.value} value={ex.value}>{ex.label}</option>
                  ))}
                </select>
                <ChevronDown size={10} color={muted}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* Spot fiyat */}
          <div>
            <div style={label}>Spot Fiyat</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={{ ...inp, flex: 1 }} value={spot}
                onChange={e => setSpot(e.target.value)}
                placeholder="Otomatik çek veya gir"
                type="number"
              />
              <button
                onClick={fetchSpot}
                disabled={fetchingSpot}
                style={{
                  background: btnBg, border: `1px solid ${accent}40`, color: accent,
                  borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4, opacity: fetchingSpot ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <RefreshCw size={10} style={{ animation: fetchingSpot ? 'spin 1s linear infinite' : 'none' }} />
                Çek
              </button>
            </div>
          </div>

          {/* Strike + Vade + Faiz */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <div style={label}>Strike</div>
              <input style={inp} value={strike} onChange={e => setStrike(e.target.value)}
                placeholder="105000" type="number" />
            </div>
            <div>
              <div style={label}>Vade (gün)</div>
              <input style={inp} value={days} onChange={e => setDays(e.target.value)}
                type="number" min="1" />
            </div>
            <div>
              <div style={label}>Faiz (%)</div>
              <input style={inp} value={rate} onChange={e => setRate(e.target.value)}
                type="number" step="0.1" />
            </div>
          </div>

          {/* Takvim + Opsiyon tipi */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={label}>Takvim</div>
              <div style={{ position: 'relative' }}>
                <select value={calendar} onChange={e => setCalendar(e.target.value as any)}
                  style={{ ...inp, paddingRight: 20, appearance: 'none', cursor: 'pointer' }}>
                  <option value="actual">Gerçek (365)</option>
                  <option value="bist">BIST (252)</option>
                  <option value="crypto">Kripto (365)</option>
                </select>
                <ChevronDown size={10} color={muted}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              </div>
            </div>
            <div>
              <div style={label}>Opsiyon Tipi</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['call', 'put'] as const).map(t => (
                  <button key={t} onClick={() => setOptType(t)} style={{
                    flex: 1, padding: '4px 0', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                    fontWeight: 600, border: `1px solid ${optType === t ? accent : border}`,
                    background: optType === t ? (t === 'call' ? posBg : negBg) : inputBg,
                    color: optType === t ? (t === 'call' ? posFg : negFg) : muted,
                    transition: 'all 0.1s',
                  }}>
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Mod seçimi: IV→Fiyat / Fiyat→IV */}
          <div>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${border}` }}>
              {(['iv2price', 'price2iv'] as const).map(m => (
                <button key={m} onClick={() => setMode(m)} style={{
                  flex: 1, padding: '5px 0', fontSize: 11, cursor: 'pointer',
                  background: mode === m ? btnBg : inputBg,
                  color: mode === m ? accent : muted,
                  border: 'none', fontWeight: mode === m ? 700 : 400,
                  transition: 'all 0.1s',
                }}>
                  {m === 'iv2price' ? 'IV → Fiyat' : 'Fiyat → IV'}
                </button>
              ))}
            </div>
          </div>

          {/* IV veya Piyasa Fiyatı */}
          {mode === 'iv2price' ? (
            <div>
              <div style={label}>Implied Volatility (%)</div>
              <input style={inp} value={iv} onChange={e => setIv(e.target.value)}
                placeholder="40.00" type="number" step="0.5" />
            </div>
          ) : (
            <div>
              <div style={label}>Piyasa Fiyatı</div>
              <input style={inp} value={mktPrice} onChange={e => setMktPrice(e.target.value)}
                placeholder="4200" type="number" />
            </div>
          )}

          {/* Hesapla butonu — girdiler dolunca otomatik hesaplanır, bu manuel tetikleyici */}
          <button onClick={calculate} style={{
            width: '100%', padding: '7px 0', borderRadius: 6, fontSize: 12, fontWeight: 700,
            background: btnBg, border: `1px solid ${accent}60`, color: accent, cursor: 'pointer',
            letterSpacing: '0.05em', transition: 'background 0.1s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = btnHover; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = btnBg; }}
          >
            HESAPLA
          </button>

          {/* Hata */}
          {error && (
            <div style={{ fontSize: 11, color: negFg, background: errBg, borderRadius: 6, padding: '6px 10px' }}>
              {error}
            </div>
          )}

          {/* Sonuçlar */}
          {result && (() => {
            const S = parseFloat(spot), K = parseFloat(strike);
            const mny = (isFinite(S) && isFinite(K) && K > 0) ? moneyness(S, K, optType) : null;
            return (
            <div style={{ borderTop: `1px solid ${border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>

              {/* Moneyness rozeti */}
              {mny && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                    color: mny.color, background: `${mny.color}1a`,
                    border: `1px solid ${mny.color}55`, borderRadius: 5, padding: '2px 8px',
                  }}>
                    {mny.label}
                  </span>
                  <span style={{ fontSize: 10, color: muted }}>
                    S/K = {(S / K).toFixed(4)}
                  </span>
                </div>
              )}

              {/* Ana metrikler */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: bgCard, borderRadius: 8, padding: '8px 12px', border: `1px solid ${border}` }}>
                  <div style={{ ...label, color: gold }}>Teorik Fiyat</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: gold }}>{fmtPrice(result.price)}</div>
                </div>
                <div style={{ background: bgCard, borderRadius: 8, padding: '8px 12px', border: `1px solid ${border}` }}>
                  <div style={{ ...label }}>IV</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: accent }}>
                    {result.iv !== null
                      ? `${(result.iv * 100).toFixed(2)}%`
                      : `${(result.sigmaUsed * 100).toFixed(2)}%`}
                  </div>
                </div>
              </div>

              {/* İç değer / Zaman değeri */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: bgCard, borderRadius: 6, padding: '6px 10px', border: `1px solid ${border}` }}>
                  <div style={label}>İç Değer</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: text }}>{fmtPrice(result.intrinsic)}</div>
                </div>
                <div style={{ background: bgCard, borderRadius: 6, padding: '6px 10px', border: `1px solid ${border}` }}>
                  <div style={label}>Zaman Değeri</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: text }}>{fmtPrice(result.timeValue)}</div>
                </div>
              </div>

              {/* Greeks */}
              <div style={{ background: bgCard, borderRadius: 8, padding: '8px 12px', border: `1px solid ${border}` }}>
                <div style={{ ...label, marginBottom: 6 }}>Greeks</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12 }}>
                  {[
                    { sym: 'Δ Delta',  val: fmt(result.greeks.delta, 4) },
                    { sym: 'Γ Gamma',  val: fmt(result.greeks.gamma, 6) },
                    { sym: 'Θ Theta',  val: fmt(result.greeks.theta, 4) + ' /gün' },
                    { sym: 'ν Vega',   val: fmt(result.greeks.vega, 4) + ' /%' },
                    { sym: 'ρ Rho',    val: fmt(result.greeks.rho, 4) + ' /%' },
                  ].map(({ sym, val }) => (
                    <div key={sym} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: muted }}>{sym}</span>
                      <span style={{ color: text, fontWeight: 600 }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* Spin animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
